import React, { useCallback, useMemo, useState } from "react";

import { faSlack } from "@awesome.me/kit-4c83d50d0d/icons/classic/brands";
import { faBell, faDesktop, faEnvelope, faInfoCircle, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { addMonths } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { compact, memoize } from "lodash";
import moment from "moment";
import { commitMutation, graphql } from "relay-runtime";

import { ReactComponent as TeamsLogo } from "~/legacy-assets/images/logos/microsoft-teams-icon.svg";
import { ReactComponent as SlackLogo } from "~/legacy-assets/images/logos/slack_icon_only.svg";
import { useEffectAfterMount, useMedia } from "~/lib/hooks";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { capitalizeFirstLetter, libStore as store } from "~/lib/utils";
import { useFirstViewNotificationFeatureFlagOn } from "~/meetingRecorder/utils";
import { environment } from "~/models";
import { useLibraryNavigationFeatureFlagOn } from "~/nav/featureFlag";
import { Button, Checkbox } from "~/ui";
import { IconButton, Switch } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import Selector from "~/ui/selectors/Selector";
import Tooltip, { type TooltipProps, useSingleton } from "~/ui/Tooltip";

import { useDesktopSetting } from "../DesktopAppSettings";
import { useDeviceNotificationsToggle } from "../useDeviceNotificationsToggle";
import BotlessRecordingDisabledApps from "./BotlessRecordingDisabledApps";
import MeetingMultiSelector, { type MeetingType } from "./MeetingMultiSelector";
import { ReactComponent as MSTeamsIcon } from "./ms-teams-16-icon.svg";
import {
	Divider,
	Label,
	Row,
	SectionTitle,
	SettingsPageSubtitle,
	SettingsPageTitle,
	sharedUserSettingsMutation,
	SubLabel,
	useUserSetting,
} from "./shared";

import type { NotificationsPreferencesQuery } from "~/__generated__/NotificationsPreferencesQuery.graphql";
import type { NotificationsSet1onNotification1WhitelistMutation } from "~/__generated__/NotificationsSet1onNotification1WhitelistMutation.graphql";
import type { NotificationsWatchStreamsRowQuery } from "~/__generated__/NotificationsWatchStreamsRowQuery.graphql";
import type { sharedUserSettingsMutation as SharedUserSettingsMutationType } from "~/__generated__/sharedUserSettingsMutation.graphql";

import styles from "./Notifications.module.css";

const UNSUBSCRIBE_ALERT_KEY = "unsubscribeAlertDismissed";

function RowIcon({ children }: React.PropsWithChildren<{}>) {
	return <div className={styles.rowIcon}>{children}</div>;
}

const set1on1NotificationWhitelistMutation = graphql`
	mutation NotificationsSet1onNotification1WhitelistMutation($input: [ID!]!) @raw_response_type {
		updateSettings(input: { impending1on1NotificationMeetingsWhitelist: $input }) {
			viewer {
				settings {
					impending1on1NotificationMeetingsWhitelist {
						id
						guid
						title
						start
						end
					}
				}
			}
		}
	}
`;

function commit1on1NotificationWhitelist(value: readonly MeetingType[]) {
	return commitMutation<NotificationsSet1onNotification1WhitelistMutation>(environment, {
		mutation: set1on1NotificationWhitelistMutation,
		variables: {
			input: value.map(v => v.id),
		},
		optimisticResponse: {
			updateSettings: {
				viewer: {
					id: "viewer",
					settings: {
						impending1on1NotificationMeetingsWhitelist: value,
					},
				},
			},
		},
	});
}

const notificationsQuery = graphql`
	query NotificationsPreferencesQuery {
		viewer {
			integrations {
				office365 {
					id
				}
			}
			settings {
				pushNotificationsEnabled
				emailNotificationsEnabled
				slackNotificationsEnabled
				teamsNotificationsEnabled
				digestObjectivesEnabled
				digestMeetingsEnabled
				digest1on1sEnabled
				digestEmailFrequency
				digestSlackFrequency
				digestTeamsFrequency
				notifyAboutMention {
					email
					push
				}
				notifyAboutAssignment {
					email
					push
				}
				notifyAboutTalkingPoint {
					email
					push
				}
				notifyAboutDueDateChange {
					email
					push
				}
				notifyAboutNoteEdited {
					email
					push
				}
				notifyAboutRecordingsFirstViewed {
					email
					push
				}
				meetingRecordingAvailableNotificationEnabled
				notifyAboutChannelsIJoined {
					email
					push
				}
				notifyAboutPostsInChannelsIWatch {
					email
					push
				}
				commentsNotifyWatchedStreams {
					email
					push
				}
				commentsNotifyReplyResolve {
					email
					push
				}
				meetingBriefsNotifyReady {
					email
					push
				}
				impending1on1NotificationDeliveryPathways
				impending1on1NotificationDeliveryTimeUnit
				impending1on1NotificationDeliveryTimeValue
				impending1on1NotificationMeetingsWhitelist {
					id
					guid
					title
					start
					end
				}
				objectivesNotifyAssigned
				objectivesNotifyUpdated
				objectivesNotifyEndOfCycleReminder
				impendingOkrDueDateNotificationEnabled
				impendingOkrDueDateNotificationDeliveryTimeUnit
				impendingOkrDueDateNotificationDeliveryTimeValue
				promptNotificationsSendViaSlack
				promptNotificationsSendViaTeams
				promptNotificationsSendViaEmail
				feedbackSummaryForMeetingsIHostEmailEnabled
				feedbackGivenToMeEmailEnabled
				feedbackSharedWithMeEmailEnabled
				feedbackNeedsActionEmailEnabled
				feedbackRequestIHaveSentEmailEnabled
				feedbackSummaryForMeetingsIHostSlackEnabled
				feedbackGivenToMeSlackEnabled
				feedbackSharedWithMeSlackEnabled
				feedbackNeedsActionSlackEnabled
				feedbackRequestIHaveSentSlackEnabled
				feedbackSummaryForMeetingsIHostTeamsEnabled
				feedbackGivenToMeTeamsEnabled
				feedbackSharedWithMeTeamsEnabled
				feedbackNeedsActionTeamsEnabled
				feedbackRequestIHaveSentTeamsEnabled
			}
			user {
				internalAttributes {
					isUnsubscribedFromEmails
					manageEmailsUrl
				}
				account {
					allowAiFeatures
				}
			}
		}
	}
`;

type ActivityNotificationSettingKey =
	| "notifyAboutMention"
	| "notifyAboutAssignment"
	| "notifyAboutTalkingPoint"
	| "notifyAboutDueDateChange"
	| "notifyAboutNoteEdited"
	| "notifyAboutChannelsIJoined"
	| "notifyAboutPostsInChannelsIWatch"
	| "commentsNotifyWatchedStreams"
	| "commentsNotifyReplyResolve"
	| "meetingBriefsNotifyReady"
	| "notifyAboutRecordingsFirstViewed";

function useActivityNotificationSetting(
	settings: NotificationsPreferencesQuery["response"]["viewer"]["settings"],
	name: ActivityNotificationSettingKey,
) {
	const uppercase = capitalizeFirstLetter(name) as Capitalize<typeof name>;

	const [push, setPush] = useUserSetting(`push${uppercase}`, settings[name].push, {
		updater: `viewer.settings.${name}.push`,
	});

	const [email, setEmail] = useUserSetting(`email${uppercase}`, settings[name].email, {
		updater: `viewer.settings.${name}.email`,
	});

	return useMemo(
		() => ({
			push: {
				value: push,
				set: setPush,
			},
			email: {
				value: email,
				set: setEmail,
			},
		}),
		[email, push, setEmail, setPush],
	);
}

type FeedbackNotificationSettingsKey =
	| "SummaryForMeetingsIHost"
	| "GivenToMe"
	| "SharedWithMe"
	| "NeedsAction"
	| "RequestIHaveSent";
function useFeedbackNotificationSetting(
	settings: NotificationsPreferencesQuery["response"]["viewer"]["settings"],
	name: FeedbackNotificationSettingsKey,
) {
	const [email, setEmail] = useUserSetting(`feedback${name}EmailEnabled`, settings[`feedback${name}EmailEnabled`], {
		updater: `viewer.settings.feedback${name}EmailEnabled`,
	});
	const [slack, setSlack] = useUserSetting(`feedback${name}SlackEnabled`, settings[`feedback${name}SlackEnabled`], {
		updater: `viewer.settings.feedback${name}SlackEnabled`,
	});
	const [teams, setTeams] = useUserSetting(`feedback${name}TeamsEnabled`, settings[`feedback${name}TeamsEnabled`], {
		updater: `viewer.settings.feedback${name}TeamsEnabled`,
	});

	return useMemo(
		() => ({
			email: {
				value: email,
				set: setEmail,
			},
			slack: {
				value: slack,
				set: setSlack,
			},
			teams: {
				value: teams,
				set: setTeams,
			},
		}),
		[email, slack, teams, setEmail, setSlack, setTeams],
	);
}

function SpacedRow({ children }: React.PropsWithChildren<{}>) {
	return (
		<tr>
			<td className={styles.spacer}></td>
			{children}
			<td className={styles.spacer}></td>
		</tr>
	);
}

interface NotificationRowChannelProp {
	value: boolean | null | undefined;
	set: (value: boolean) => void;
}
type NotificationRowProps = React.PropsWithChildren<{
	shownColumns: ("push" | "email" | "slack" | "teams")[];
	push?: NotificationRowChannelProp;
	email?: NotificationRowChannelProp;
	slack?: NotificationRowChannelProp;
	teams?: NotificationRowChannelProp;
}>;

function NotificationRow({ children, shownColumns, push, email, slack, teams }: NotificationRowProps) {
	return (
		<SpacedRow>
			<th scope="row">{children}</th>
			{shownColumns.length === 0 && <DisabledCheckboxCell />}
			{shownColumns.includes("push") &&
				(push ? <CheckboxCell value={push.value} set={push.set} /> : <DisabledCheckboxCell />)}
			{shownColumns.includes("email") &&
				(email ? <CheckboxCell value={email.value} set={email.set} /> : <DisabledCheckboxCell />)}
			{shownColumns.includes("slack") &&
				(slack ? <CheckboxCell value={slack.value} set={slack.set} /> : <DisabledCheckboxCell />)}
			{shownColumns.includes("teams") &&
				(teams ? <CheckboxCell value={teams.value} set={teams.set} /> : <DisabledCheckboxCell />)}
		</SpacedRow>
	);
}

function TableColumnHeading({
	icon,
	name,
	singleton,
}: {
	icon: React.ReactNode;
	name: string;
	singleton: TooltipProps["singleton"];
}) {
	const displayAsIcon = useMedia("(max-width: 735px)");
	return displayAsIcon ? (
		<Tooltip content={name} singleton={singleton}>
			<th scope="col">{icon}</th>
		</Tooltip>
	) : (
		<th scope="col">{name}</th>
	);
}

function TableSectionHeading({ children, numberOfColumns }: React.PropsWithChildren<{ numberOfColumns: number }>) {
	return (
		<SpacedRow>
			<th scope="rowgroup" colSpan={numberOfColumns}>
				{children}
			</th>
		</SpacedRow>
	);
}

function CheckboxCell({ value, set }: { value: boolean | null | undefined; set: (value: boolean) => void }) {
	return (
		<td className={styles.checkboxCell}>
			<Checkbox checked={value ?? false} onCheckedChange={set} />
		</td>
	);
}

function DisabledCheckboxCell() {
	return (
		<td className={styles.checkboxCell}>
			<Checkbox disabled checked="indeterminate" />
		</td>
	);
}

const impending1on1NotificationTimeUnitOptions = [
	{ value: "minutes", label: "minutes" },
	{ value: "hours", label: "hours" },
] as const;

function isImpending1on1SettingValid(timeValue: number | undefined, timeUnit: string | undefined): boolean {
	if (timeValue === undefined || timeUnit === undefined) return false;
	if (timeUnit === "minutes") return timeValue >= 5;
	if (timeUnit === "hours") return timeValue >= 1;
	return false;
}

const impendingOkrDueDateTimeUnitOptions = [
	{ value: "days", label: "days" },
	{ value: "weeks", label: "weeks" },
] as const;

function isImpendingOkrSettingValid(timeValue: number | undefined): boolean {
	return timeValue !== undefined && timeValue >= 1;
}

function commitSettingsUpdate(input: SharedUserSettingsMutationType["variables"]["input"]) {
	commitMutation<SharedUserSettingsMutationType>(environment, {
		mutation: sharedUserSettingsMutation,
		variables: { input },
	});
}

function NotificationSettings() {
	const data = useLazyLoadQuery<NotificationsPreferencesQuery>(notificationsQuery, {}, { fetchPolicy: "network-only" });

	const { settings, user } = data.viewer;

	const hasOffice365 = !!data.viewer.integrations.office365?.id;

	const [showUnsubscribeAlert, setShowUnsubscribeAlert] = React.useState(
		data.viewer.user.internalAttributes?.isUnsubscribedFromEmails && !store.has(UNSUBSCRIBE_ALERT_KEY),
	);
	const dismissUnsubscribeAlert = React.useCallback(() => {
		dispatchAnalyticsTrack("[Unsubscribe Alert] Dismissed");
		store.setExpire(UNSUBSCRIBE_ALERT_KEY, true, moment().add(3, "month"));
		setShowUnsubscribeAlert(false);
	}, []);
	const onManageEmails = React.useCallback(() => {
		if (!user.internalAttributes) return;
		dispatchAnalyticsTrack("[Unsubscribe Alert] Manage Emails Clicked");
		window.open(user.internalAttributes.manageEmailsUrl, "_blank", "noopener noreferrer");
	}, [user]);
	const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useUserSetting(
		"emailNotificationsEnabled",
		settings.emailNotificationsEnabled ?? true,
		{ updater: true },
	);
	const [pushNotificationsEnabled, setPushNotificationsEnabled] = useUserSetting(
		"pushNotificationsEnabled",
		settings.pushNotificationsEnabled ?? true,
		{ updater: true },
	);
	const [slackNotificationsEnabled, setSlackNotificationsEnabled] = useUserSetting(
		"slackNotificationsEnabled",
		settings.slackNotificationsEnabled ?? false,
		{ updater: true },
	);
	const [teamsNotificationsEnabled, setTeamsNotificationsEnabled] = useUserSetting(
		"teamsNotificationsEnabled",
		settings.teamsNotificationsEnabled ?? false,
		{ updater: true },
	);

	const botlessRecorderSettings = useDesktopSetting("botlessRecorder");
	const isAutoDetectMeetingsEnabled = botlessRecorderSettings ? botlessRecorderSettings.value.autoDetectEnabled : false;
	const setAutoDetectMeetingsEnabled = useCallback(
		(newValue: boolean) => {
			if (!botlessRecorderSettings) throw new Error("Can't set botless recorder setting");
			botlessRecorderSettings.update({
				autoDetectEnabled: newValue,
				disabledApps: botlessRecorderSettings.value.disabledApps ?? [],
			});
		},
		[botlessRecorderSettings],
	);

	const [showNotificationsToggle, enablePushButton] = useDeviceNotificationsToggle(pushNotificationsEnabled ?? false);

	const [columnTippySource, columnTippyTarget] = useSingleton();

	const [digestEmailFrequency, setDigestEmailFrequency] = useUserSetting(
		"digestEmailFrequency",
		settings.digestEmailFrequency,
		{ updater: true },
	);
	const [digestSlackFrequency, setDigestSlackFrequency] = useUserSetting(
		"digestSlackFrequency",
		settings.digestSlackFrequency,
		{ updater: true },
	);
	const [digestTeamsFrequency, setDigestTeamsFrequency] = useUserSetting(
		"digestTeamsFrequency",
		settings.digestTeamsFrequency,
		{ updater: true },
	);
	const digestEmailFrequencySetter = useMemo(
		() =>
			memoize((frequency: string) => {
				return (checked: boolean) => {
					setDigestEmailFrequency(checked ? frequency : "never");
				};
			}),
		[setDigestEmailFrequency],
	);
	const digestSlackFrequencySetter = useMemo(
		() =>
			memoize((frequency: string) => {
				return (checked: boolean) => {
					setDigestSlackFrequency(checked ? frequency : "never");
				};
			}),
		[setDigestSlackFrequency],
	);
	const digestTeamsFrequencySetter = useMemo(
		() =>
			memoize((frequency: string) => {
				return (checked: boolean) => {
					setDigestTeamsFrequency(checked ? frequency : "never");
				};
			}),
		[setDigestTeamsFrequency],
	);

	const [digest1on1sEnabled, setDigest1on1sEnabled] = useUserSetting(
		"digest1on1sEnabled",
		settings.digest1on1sEnabled ?? true,
		{ updater: true },
	);

	const [digestMeetingsEnabled, setDigestMeetingsEnabled] = useUserSetting(
		"digestMeetingsEnabled",
		settings.digestMeetingsEnabled ?? true,
		{ updater: true },
	);

	const [digestObjectivesEnabled, setDigestObjectivesEnabled] = useUserSetting(
		"digestObjectivesEnabled",
		settings.digestObjectivesEnabled ?? true,
		{ updater: true },
	);

	const notifyAboutMention = useActivityNotificationSetting(settings, "notifyAboutMention");
	const notifyAboutTalkingPoint = useActivityNotificationSetting(settings, "notifyAboutTalkingPoint");
	const notifyAboutAssignment = useActivityNotificationSetting(settings, "notifyAboutAssignment");
	const notifyAboutDueDateChange = useActivityNotificationSetting(settings, "notifyAboutDueDateChange");
	const notifyAboutNoteEdited = useActivityNotificationSetting(settings, "notifyAboutNoteEdited");

	const [meetingRecordingEnabled, setMeetingRecordingEnabled] = useUserSetting(
		"meetingRecordingAvailableNotificationEnabled",
		settings.meetingRecordingAvailableNotificationEnabled ?? true,
		{ updater: true },
	);

	const notifyAboutChannelsIJoined = useActivityNotificationSetting(settings, "notifyAboutChannelsIJoined");
	const notifyAboutPostsInChannelsIWatch = useActivityNotificationSetting(settings, "notifyAboutPostsInChannelsIWatch");

	const commentsNotifyWatchedStreams = useActivityNotificationSetting(settings, "commentsNotifyWatchedStreams");
	const commentsNotifyReplyResolve = useActivityNotificationSetting(settings, "commentsNotifyReplyResolve");

	const meetingBriefsFeatureIsOn = useFeatureIsOn("meeting-briefs");
	const meetingBriefsNotifyReady = useActivityNotificationSetting(settings, "meetingBriefsNotifyReady");
	const notifyAboutRecordingsFirstViewed = useActivityNotificationSetting(settings, "notifyAboutRecordingsFirstViewed");

	const feedbackSummaryForMeetingsIHost = useFeedbackNotificationSetting(settings, "SummaryForMeetingsIHost");
	const feedbackGivenToMe = useFeedbackNotificationSetting(settings, "GivenToMe");
	const feedbackSharedWithMe = useFeedbackNotificationSetting(settings, "SharedWithMe");
	const feedbackNeedsAction = useFeedbackNotificationSetting(settings, "NeedsAction");
	const feedbackRequestIHaveSent = useFeedbackNotificationSetting(settings, "RequestIHaveSent");

	/** The setting value as stored in Relay -- we have to memo this, otherwise it creates a new array every time
	 * we re-render, which it views as an update, causing it to re-submit the settings mutation */
	const storedImpending1on1NotificationDeliveryPathways = useMemo(
		() => compact(settings.impending1on1NotificationDeliveryPathways),
		[settings.impending1on1NotificationDeliveryPathways],
	);
	const [impending1on1NotificationDeliveryPathways, setImpending1on1NotificationDeliveryPathways] = useUserSetting(
		"impending1on1NotificationDeliveryPathways",
		storedImpending1on1NotificationDeliveryPathways,
		{
			updater: true,
		},
	);

	const [impending1on1NotificationDeliveryTimeValue, setImpending1on1NotificationDeliveryTimeValue] = useState(
		settings.impending1on1NotificationDeliveryTimeValue ?? undefined,
	);
	const [impending1on1NotificationDeliveryTimeUnit, setImpending1on1NotificationDeliveryTimeUnit] = useState(
		settings.impending1on1NotificationDeliveryTimeUnit ?? "minutes",
	);
	const on1on1TimeValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setImpending1on1NotificationDeliveryTimeValue(parseInt(e.target.value, 10));
	}, []);
	const on1on1TimeUnitChange = useCallback(
		(option: (typeof impending1on1NotificationTimeUnitOptions)[number] | null) => {
			if (!option) throw new Error("Field must be set");
			if (option.value === impending1on1NotificationDeliveryTimeUnit) return;
			setImpending1on1NotificationDeliveryTimeUnit(option.value);
			setImpending1on1NotificationDeliveryTimeValue((prev = 0) => {
				if (option.value === "hours") return Math.ceil(prev / 60);
				else return Math.max(prev * 60, 5);
			});
		},
		[impending1on1NotificationDeliveryTimeUnit],
	);
	useEffectAfterMount(() => {
		if (
			!isImpending1on1SettingValid(
				impending1on1NotificationDeliveryTimeValue,
				impending1on1NotificationDeliveryTimeUnit,
			)
		)
			return;
		const debounceTimeout = setTimeout(
			() =>
				commitSettingsUpdate({
					impending1on1NotificationDeliveryTimeValue,
					impending1on1NotificationDeliveryTimeUnit,
				}),
			300,
		);
		return () => clearTimeout(debounceTimeout);
	}, [impending1on1NotificationDeliveryTimeUnit, impending1on1NotificationDeliveryTimeValue]);

	const meetingSelectorStart = useMemo(() => new Date(), []);
	const meetingSelectorEnd = useMemo(() => addMonths(new Date(), 3), []);

	const [impendingOkrDueDateNotificationEnabled, setImpendingOkrDueDateNotificationEnabled] = useUserSetting(
		"impendingOkrDueDateNotificationEnabled",
		settings.impendingOkrDueDateNotificationEnabled ?? true,
		{ updater: true },
	);
	const [impendingOkrDueDateNotificationDeliveryTimeValue, setImpendingOkrDueDateNotificationDeliveryTimeValue] =
		useState(settings.impendingOkrDueDateNotificationDeliveryTimeValue ?? undefined);
	const [impendingOkrDueDateNotificationDeliveryTimeUnit, setImpendingOkrDueDateNotificationDeliveryTimeUnit] =
		useState(settings.impendingOkrDueDateNotificationDeliveryTimeUnit ?? "days");
	const onImpendingOkrDueDateTimeValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setImpendingOkrDueDateNotificationDeliveryTimeValue(parseInt(e.target.value, 10));
	}, []);
	const onImpendingOkrDueDateTimeUnitChange = useCallback(
		(option: (typeof impendingOkrDueDateTimeUnitOptions)[number] | null) => {
			if (!option) throw new Error("Field must be set");
			if (option.value === impendingOkrDueDateNotificationDeliveryTimeUnit) return;
			setImpendingOkrDueDateNotificationDeliveryTimeUnit(option.value);
			setImpendingOkrDueDateNotificationDeliveryTimeValue((prev = 0) => {
				if (option.value === "weeks") return Math.ceil(prev / 7);
				else return Math.max(prev * 7, 1);
			});
		},
		[impendingOkrDueDateNotificationDeliveryTimeUnit],
	);
	useEffectAfterMount(() => {
		if (!isImpendingOkrSettingValid(impendingOkrDueDateNotificationDeliveryTimeValue)) return;
		const debounceTimeout = setTimeout(
			() =>
				commitSettingsUpdate({
					impendingOkrDueDateNotificationDeliveryTimeValue,
					impendingOkrDueDateNotificationDeliveryTimeUnit,
				}),
			300,
		);
		return () => clearTimeout(debounceTimeout);
	}, [impendingOkrDueDateNotificationDeliveryTimeUnit, impendingOkrDueDateNotificationDeliveryTimeValue]);

	const setSingleImpending1on1Pathway = useCallback(
		(pathway: string, value: boolean) => {
			const existingPathways = new Set(impending1on1NotificationDeliveryPathways);
			if (value) {
				existingPathways.add(pathway);
			} else {
				existingPathways.delete(pathway);
			}
			setImpending1on1NotificationDeliveryPathways(compact(Array.from(existingPathways.keys())));
		},
		[impending1on1NotificationDeliveryPathways, setImpending1on1NotificationDeliveryPathways],
	);
	const setImpending1on1Email = useMemo(
		() => setSingleImpending1on1Pathway.bind(null, "email"),
		[setSingleImpending1on1Pathway],
	);
	const setImpending1on1Slack = useMemo(
		() => setSingleImpending1on1Pathway.bind(null, "slack"),
		[setSingleImpending1on1Pathway],
	);

	const [objectivesNotifyAssigned, setObjectivesNotifyAssigned] = useUserSetting(
		"objectivesNotifyAssigned",
		settings.objectivesNotifyAssigned ?? false,
		{ updater: true },
	);
	const [objectivesNotifyUpdated, setObjectivesNotifyUpdated] = useUserSetting(
		"objectivesNotifyUpdated",
		settings.objectivesNotifyUpdated ?? false,
		{ updater: true },
	);
	const [objectivesNotifyEndOfCycleReminder, setObjectivesNotifyEndOfCycleReminder] = useUserSetting(
		"objectivesNotifyEndOfCycleReminder",
		settings.objectivesNotifyEndOfCycleReminder ?? false,
		{
			updater: true,
		},
	);

	const [promptNotificationsSendViaEmail, setPromptNotificationsSendViaEmail] = useUserSetting(
		"promptNotificationsSendViaEmail",
		settings.promptNotificationsSendViaEmail ?? false,
		{
			updater: true,
		},
	);
	const [promptNotificationsSendViaSlack, setPromptNotificationsSendViaSlack] = useUserSetting(
		"promptNotificationsSendViaSlack",
		settings.promptNotificationsSendViaSlack ?? false,
		{
			updater: true,
		},
	);
	const [promptNotificationsSendViaTeams, setPromptNotificationsSendViaTeams] = useUserSetting(
		"promptNotificationsSendViaTeams",
		settings.promptNotificationsSendViaTeams ?? false,
		{
			updater: true,
		},
	);

	const shownColumns = useMemo(() => {
		const shownColumns: NotificationRowProps["shownColumns"] = [];
		if (pushNotificationsEnabled) shownColumns.push("push");
		if (emailNotificationsEnabled) shownColumns.push("email");
		if (slackNotificationsEnabled) shownColumns.push("slack");
		if (teamsNotificationsEnabled && hasOffice365) shownColumns.push("teams");
		return shownColumns;
	}, [
		emailNotificationsEnabled,
		hasOffice365,
		pushNotificationsEnabled,
		slackNotificationsEnabled,
		teamsNotificationsEnabled,
	]);
	const numberOfChannelColumns = Math.max(shownColumns.length, 1);
	const numberOfColumns = numberOfChannelColumns + 1;

	const libraryNavigationFeatureFlagOn = useLibraryNavigationFeatureFlagOn();
	const firstViewNotificationFeatureFlagOn = useFirstViewNotificationFeatureFlagOn();

	return (
		<>
			<AnimatePresence>
				{showUnsubscribeAlert && (
					<motion.div
						key="table"
						initial={{ height: 0 }}
						animate={{ height: "auto" }}
						exit={{ height: 0 }}
						transition={{ ease: "easeInOut", duration: 0.5 }}
						style={{ overflow: "hidden" }}
					>
						<div className={styles.unsubscribeAlert}>
							<p>
								<b>You are currently unsubscribed from one or more emails.</b> This could prevent you from receiving
								certain notifications.
							</p>
							<Button variant="primary" size="normal" className={styles.manageBtn} onClick={onManageEmails}>
								Fix this
							</Button>
							<IconButton icon={faTimes} onClick={dismissUnsubscribeAlert} />
						</div>
					</motion.div>
				)}
			</AnimatePresence>
			<SectionTitle>Notification methods</SectionTitle>
			<Row>
				<RowIcon>
					<FontAwesomeIcon icon={faDesktop} />
				</RowIcon>
				<Row.Left>
					<Label>Push notifications</Label>
					<SubLabel>Receive mobile, browser, and desktop notifications</SubLabel>
				</Row.Left>
				<Row.Right>
					<Switch
						size="small"
						checked={pushNotificationsEnabled ?? true}
						onCheckedChange={setPushNotificationsEnabled}
					/>
				</Row.Right>
			</Row>
			{showNotificationsToggle && (
				<Row>
					<RowIcon />
					{typeof enablePushButton === "string" ? (
						<Row.Left>
							<Label>{enablePushButton}</Label>
						</Row.Left>
					) : (
						<>
							<Row.Left>
								<Label>Enable push notifications on this device</Label>
							</Row.Left>
							<Row.Right>{enablePushButton}</Row.Right>
						</>
					)}
				</Row>
			)}
			<Divider />
			<Row>
				<RowIcon>
					<FontAwesomeIcon icon={faEnvelope} />
				</RowIcon>
				<Row.Left>
					<Label>Email notifications</Label>
					<SubLabel>Receive email notifications</SubLabel>
				</Row.Left>
				<Row.Right>
					<Switch
						size="small"
						checked={emailNotificationsEnabled ?? true}
						onCheckedChange={setEmailNotificationsEnabled}
					/>
				</Row.Right>
			</Row>
			<Divider />
			<Row>
				<RowIcon>
					<SlackLogo style={{ width: "1.5rem", height: "1.5rem" }} />
				</RowIcon>
				<Row.Left>
					<Label>Slack</Label>
					<SubLabel>Receive personal notifications in Slack</SubLabel>
				</Row.Left>
				<Row.Right>
					<Switch
						size="small"
						checked={slackNotificationsEnabled ?? false}
						onCheckedChange={setSlackNotificationsEnabled}
					/>
				</Row.Right>
			</Row>
			{hasOffice365 && (
				<>
					<Divider />
					<Row>
						<RowIcon>
							<TeamsLogo style={{ width: "1.5rem", height: "1.5rem" }} />
						</RowIcon>
						<Row.Left>
							<Label>Microsoft Teams</Label>
							<SubLabel>Receive personal notifications in Microsoft Teams</SubLabel>
						</Row.Left>
						<Row.Right>
							<Switch
								size="small"
								checked={teamsNotificationsEnabled ?? false}
								onCheckedChange={setTeamsNotificationsEnabled}
							/>
						</Row.Right>
					</Row>
				</>
			)}
			{botlessRecorderSettings && (
				<>
					<Divider />
					<Row>
						<Row.Left>
							<Label>Auto-detect meetings</Label>
							<SubLabel>Show a notification when a call is detected</SubLabel>
						</Row.Left>
						<Row.Right>
							<Switch
								size="small"
								checked={isAutoDetectMeetingsEnabled ?? false}
								onCheckedChange={setAutoDetectMeetingsEnabled}
							/>
						</Row.Right>
					</Row>
					{isAutoDetectMeetingsEnabled && (
						<Row>
							<BotlessRecordingDisabledApps />
						</Row>
					)}
				</>
			)}
			<Divider />
			<SectionTitle>Notification settings</SectionTitle>
			<div className={styles.methodTableWrapper}>
				<table className={styles.methodTable}>
					<thead>
						<tr>
							<th className={styles.spacer}></th>
							<th scope="col">Notification type</th>
							<Tooltip singleton={columnTippySource} />
							{(shownColumns.includes("push") || shownColumns.length === 0) && (
								<TableColumnHeading
									icon={<FontAwesomeIcon icon={faBell} />}
									name="Push"
									singleton={columnTippyTarget}
								/>
							)}
							{shownColumns.includes("email") && (
								<TableColumnHeading
									icon={<FontAwesomeIcon icon={faEnvelope} />}
									name="Email"
									singleton={columnTippyTarget}
								/>
							)}
							{shownColumns.includes("slack") && (
								<TableColumnHeading
									icon={<FontAwesomeIcon icon={faSlack} />}
									name="Slack"
									singleton={columnTippyTarget}
								/>
							)}
							{shownColumns.includes("teams") && (
								<TableColumnHeading
									icon={<MSTeamsIcon className="svg-inline--fa fa-w-16" aria-hidden />}
									name="Teams"
									singleton={columnTippyTarget}
								/>
							)}
							<th className={styles.spacer}></th>
						</tr>
					</thead>
					<tbody>
						<TableSectionHeading numberOfColumns={numberOfColumns}>Digest</TableSectionHeading>
						<NotificationRow
							shownColumns={shownColumns}
							email={{
								value: digestEmailFrequency === "daily",
								set: digestEmailFrequencySetter("daily"),
							}}
							slack={{
								value: digestSlackFrequency === "daily",
								set: digestSlackFrequencySetter("daily"),
							}}
							teams={{
								value: digestTeamsFrequency === "daily",
								set: digestTeamsFrequencySetter("daily"),
							}}
						>
							Send me a daily digest
						</NotificationRow>
						<SpacedRow>
							<td colSpan={numberOfColumns}>
								<div className={styles.methodTableDetails}>
									<label className={styles.methodTableDetailCheckbox}>
										<Switch
											size="tiny"
											checked={digest1on1sEnabled ?? undefined}
											onCheckedChange={setDigest1on1sEnabled}
											disabled={
												!(
													(shownColumns.includes("email") && digestEmailFrequency === "daily") ||
													(shownColumns.includes("slack") && digestSlackFrequency === "daily") ||
													(shownColumns.includes("teams") && digestTeamsFrequency === "daily")
												)
											}
										/>
										Include upcoming 1-on-1s in digests
									</label>
									<label className={styles.methodTableDetailCheckbox}>
										<Switch
											size="tiny"
											checked={digestMeetingsEnabled ?? undefined}
											onCheckedChange={setDigestMeetingsEnabled}
											disabled={
												!(
													(shownColumns.includes("email") && digestEmailFrequency === "daily") ||
													(shownColumns.includes("slack") && digestSlackFrequency === "daily") ||
													(shownColumns.includes("teams") && digestTeamsFrequency === "daily")
												)
											}
										/>
										Include upcoming meetings in daily digests
									</label>
								</div>
							</td>
						</SpacedRow>
						<NotificationRow
							shownColumns={shownColumns}
							email={{
								value: digestEmailFrequency === "weekly",
								set: digestEmailFrequencySetter("weekly"),
							}}
							slack={{
								value: digestSlackFrequency === "weekly",
								set: digestSlackFrequencySetter("weekly"),
							}}
							teams={{
								value: digestTeamsFrequency === "weekly",
								set: digestTeamsFrequencySetter("weekly"),
							}}
						>
							Send me a weekly digest
						</NotificationRow>
						<SpacedRow>
							<td colSpan={numberOfColumns}>
								<div className={styles.methodTableDetails}>
									<label className={styles.methodTableDetailCheckbox}>
										<Switch
											size="tiny"
											checked={digest1on1sEnabled ?? undefined}
											onCheckedChange={setDigest1on1sEnabled}
											disabled={
												!(
													(shownColumns.includes("email") && digestEmailFrequency === "weekly") ||
													(shownColumns.includes("slack") && digestSlackFrequency === "weekly") ||
													(shownColumns.includes("teams") && digestTeamsFrequency === "weekly")
												)
											}
										/>
										Include upcoming 1-on-1s in digests
									</label>
									<label className={styles.methodTableDetailCheckbox}>
										<Switch
											size="tiny"
											checked={digestObjectivesEnabled ?? undefined}
											onCheckedChange={setDigestObjectivesEnabled}
											disabled={
												!(
													(shownColumns.includes("email") && digestEmailFrequency === "weekly") ||
													(shownColumns.includes("slack") && digestSlackFrequency === "weekly") ||
													(shownColumns.includes("teams") && digestTeamsFrequency === "weekly")
												)
											}
										/>
										Include objectives in weekly digests
									</label>
								</div>
							</td>
						</SpacedRow>
					</tbody>
					<tbody>
						<TableSectionHeading numberOfColumns={numberOfColumns}>Notes</TableSectionHeading>
						<NotificationRow shownColumns={shownColumns} {...notifyAboutMention}>
							I’m mentioned in a note
						</NotificationRow>
						<NotificationRow shownColumns={shownColumns} {...notifyAboutTalkingPoint}>
							I’m assigned a talking point
						</NotificationRow>
						<NotificationRow shownColumns={shownColumns} {...notifyAboutAssignment}>
							I’m assigned an action item
						</NotificationRow>
						<NotificationRow shownColumns={shownColumns} {...notifyAboutDueDateChange}>
							Due dates change for action items assigned to me
						</NotificationRow>
						<NotificationRow shownColumns={shownColumns} {...notifyAboutNoteEdited}>
							Notes edited in a note series I’m watching
						</NotificationRow>
						<NotificationRow
							shownColumns={shownColumns}
							email={{ value: meetingRecordingEnabled, set: setMeetingRecordingEnabled }}
						>
							Recordings for my meetings become available
						</NotificationRow>
					</tbody>
					{libraryNavigationFeatureFlagOn && (
						<tbody>
							<TableSectionHeading numberOfColumns={numberOfColumns}>Channels</TableSectionHeading>
							<NotificationRow shownColumns={shownColumns} {...notifyAboutChannelsIJoined}>
								I’m added to a channel
							</NotificationRow>
							<NotificationRow shownColumns={shownColumns} {...notifyAboutPostsInChannelsIWatch}>
								Notes, Recordings or Clips are added to a channel I’m watching
							</NotificationRow>
						</tbody>
					)}
					<tbody>
						<TableSectionHeading numberOfColumns={numberOfColumns}>Comments</TableSectionHeading>
						<NotificationRow shownColumns={shownColumns} {...commentsNotifyWatchedStreams}>
							Comments on note series that I’m watching
						</NotificationRow>
						<NotificationRow shownColumns={shownColumns} {...commentsNotifyReplyResolve}>
							Someone replies to or resolves my comment
						</NotificationRow>
					</tbody>
					<tbody>
						<TableSectionHeading numberOfColumns={numberOfColumns}>Feedback</TableSectionHeading>
						<NotificationRow shownColumns={shownColumns} {...feedbackSummaryForMeetingsIHost}>
							Meeting summary feedback for meetings I host
						</NotificationRow>
						<NotificationRow shownColumns={shownColumns} {...feedbackGivenToMe}>
							Feedback given to me
						</NotificationRow>
						<NotificationRow shownColumns={shownColumns} {...feedbackSharedWithMe}>
							Feedback shared with me
						</NotificationRow>
						<NotificationRow shownColumns={shownColumns} {...feedbackNeedsAction}>
							Requests that need my action
						</NotificationRow>
						<NotificationRow shownColumns={shownColumns} {...feedbackRequestIHaveSent}>
							Responses to requests I’ve sent
						</NotificationRow>
					</tbody>
					<tbody>
						<TableSectionHeading numberOfColumns={numberOfColumns}>Upcoming 1-on-1s</TableSectionHeading>
						<NotificationRow
							shownColumns={shownColumns}
							email={{
								value: impending1on1NotificationDeliveryPathways?.includes("email"),
								set: setImpending1on1Email,
							}}
							slack={{
								value: impending1on1NotificationDeliveryPathways?.includes("slack"),
								set: setImpending1on1Slack,
							}}
						>
							Receive notifications via…
						</NotificationRow>
						<SpacedRow>
							<td colSpan={numberOfColumns}>
								<div className={styles.methodTableDetails}>
									<div className={styles.methodTableDetailRow}>
										<div>
											Notify ahead of meetings by… &ensp;
											<Tooltip content="Cannot be shorter than 5 minutes">
												<FontAwesomeIcon icon={faInfoCircle} tabIndex={0} />
											</Tooltip>
										</div>
										<div className={styles.combinationInput}>
											<input
												className={styles.combinationInputField}
												type="number"
												value={impending1on1NotificationDeliveryTimeValue}
												onChange={on1on1TimeValueChange}
												min={impending1on1NotificationDeliveryTimeUnit === "hours" ? 1 : 5}
												required
												step={1}
												disabled={
													!(shownColumns.includes("email") || shownColumns.includes("slack")) ||
													impending1on1NotificationDeliveryPathways?.length === 0
												}
											/>
											<Selector
												width="5.6rem"
												options={impending1on1NotificationTimeUnitOptions}
												value={impending1on1NotificationTimeUnitOptions.find(
													({ value }) => value === impending1on1NotificationDeliveryTimeUnit,
												)}
												onChange={on1on1TimeUnitChange}
												isClearable={false}
												className={styles.combinationInputSelector}
												classNamePrefix="combinationInputSelector"
												condensed
												menuPlacement="auto"
												isDisabled={
													!(shownColumns.includes("email") || shownColumns.includes("slack")) ||
													impending1on1NotificationDeliveryPathways?.length === 0
												}
											/>
										</div>
									</div>
									<div className={styles.methodTableDetailRow}>
										<div>
											Only notify about the following recurring meetings: &ensp;
											{/* eslint-disable-next-line max-len */}
											<Tooltip content="Fellow will notify about any 1-on-1 meetings, recurring or not, if this list is empty">
												<FontAwesomeIcon icon={faInfoCircle} tabIndex={0} />
											</Tooltip>
										</div>
										<div className={styles.methodTableDetailRight}>
											<MeetingMultiSelector
												start={meetingSelectorStart}
												end={meetingSelectorEnd}
												value={settings.impending1on1NotificationMeetingsWhitelist}
												onChange={commit1on1NotificationWhitelist}
												isDisabled={
													!(shownColumns.includes("email") || shownColumns.includes("slack")) ||
													impending1on1NotificationDeliveryPathways?.length === 0
												}
											/>
										</div>
									</div>
								</div>
							</td>
						</SpacedRow>
					</tbody>
					<tbody>
						<TableSectionHeading numberOfColumns={numberOfColumns}>Objectives</TableSectionHeading>
						<NotificationRow
							shownColumns={shownColumns}
							email={{
								value: objectivesNotifyAssigned,
								set: setObjectivesNotifyAssigned,
							}}
						>
							I’m assigned to an objective
						</NotificationRow>
						<NotificationRow
							shownColumns={shownColumns}
							email={{
								value: objectivesNotifyUpdated,
								set: setObjectivesNotifyUpdated,
							}}
						>
							Updated objective or key result I’m contributing to
						</NotificationRow>
						<NotificationRow
							shownColumns={shownColumns}
							email={{
								value: objectivesNotifyEndOfCycleReminder,
								set: setObjectivesNotifyEndOfCycleReminder,
							}}
						>
							Send me a reminder 30 days before the end of a cycle
						</NotificationRow>
						<NotificationRow
							shownColumns={shownColumns}
							email={{
								value: impendingOkrDueDateNotificationEnabled,
								set: setImpendingOkrDueDateNotificationEnabled,
							}}
						>
							Remind me when Objectives or Key results are due
						</NotificationRow>
						<SpacedRow>
							<td colSpan={numberOfColumns}>
								<div className={styles.methodTableDetails}>
									<div className={styles.methodTableDetailRow}>
										<div>Remind me ahead of OKRs’ due date by…</div>
										<div className={styles.combinationInput}>
											<input
												className={styles.combinationInputField}
												type="number"
												value={impendingOkrDueDateNotificationDeliveryTimeValue}
												onChange={onImpendingOkrDueDateTimeValueChange}
												min={1}
												required
												step={1}
												disabled={!impendingOkrDueDateNotificationEnabled || !shownColumns.includes("email")}
											/>
											<Selector
												width="5.6rem"
												options={impendingOkrDueDateTimeUnitOptions}
												value={impendingOkrDueDateTimeUnitOptions.find(
													({ value }) => value === impendingOkrDueDateNotificationDeliveryTimeUnit,
												)}
												onChange={onImpendingOkrDueDateTimeUnitChange}
												isClearable={false}
												className={styles.combinationInputSelector}
												classNamePrefix="combinationInputSelector"
												condensed
												menuPlacement="auto"
												isDisabled={!impendingOkrDueDateNotificationEnabled || !shownColumns.includes("email")}
											/>
										</div>
									</div>
								</div>
							</td>
						</SpacedRow>
					</tbody>
					<tbody>
						<TableSectionHeading numberOfColumns={numberOfColumns}>Section headers</TableSectionHeading>
						<NotificationRow
							shownColumns={shownColumns}
							email={{
								value: promptNotificationsSendViaEmail,
								set: setPromptNotificationsSendViaEmail,
							}}
							slack={{
								value: promptNotificationsSendViaSlack,
								set: setPromptNotificationsSendViaSlack,
							}}
							teams={{
								value: promptNotificationsSendViaTeams,
								set: setPromptNotificationsSendViaTeams,
							}}
						>
							Remind me to add an agenda section via…
						</NotificationRow>
					</tbody>
					{user.account.allowAiFeatures && (meetingBriefsFeatureIsOn || firstViewNotificationFeatureFlagOn) && (
						<tbody>
							<TableSectionHeading numberOfColumns={numberOfColumns}>Note Taker</TableSectionHeading>
							{meetingBriefsFeatureIsOn && (
								<NotificationRow shownColumns={shownColumns} {...meetingBriefsNotifyReady}>
									Send me meeting briefs via…
								</NotificationRow>
							)}
							{firstViewNotificationFeatureFlagOn && (
								<NotificationRow shownColumns={shownColumns} {...notifyAboutRecordingsFirstViewed}>
									My meetings with AI notes and recordings are first viewed…
								</NotificationRow>
							)}
						</tbody>
					)}
				</table>
			</div>
			<SectionTitle>Series</SectionTitle>
			<WatchStreamsRow />
		</>
	);
}

function WatchStreamsRow() {
	const { viewer } = useLazyLoadQuery<NotificationsWatchStreamsRowQuery>(
		graphql`
			query NotificationsWatchStreamsRowQuery {
				viewer {
					settings {
						automaticallyWatchNotestreams
					}
				}
			}
		`,
		{},
		{},
	);

	const [autowatch, setAutowatch] = useUserSetting(
		"automaticallyWatchNotestreams",
		viewer.settings.automaticallyWatchNotestreams,
		{
			updater: true,
		},
	);
	return (
		<Row>
			<Row.Left>
				<Label>Automatically watch note series that I contribute to</Label>
			</Row.Left>
			<Row.Right>
				<Switch size="small" checked={autowatch ?? true} onCheckedChange={setAutowatch} />
			</Row.Right>
		</Row>
	);
}

export default function Notifications() {
	return (
		<div className={styles.main}>
			<SettingsPageTitle>Notifications</SettingsPageTitle>
			<SettingsPageSubtitle>Select when and where you’ll be notified</SettingsPageSubtitle>
			<React.Suspense fallback={<LoadingSpinner dimmer={false} inline="centered" />}>
				<NotificationSettings />
			</React.Suspense>
		</div>
	);
}
